import { attr, hasMany } from "@ember-data/model";
import { service } from "@ember/service";
import type HealthCentre from "parog-web/models/health-centre";
import type MedicsPatient from "parog-web/models/medics-patient";
import type TermsOfUseService from "parog-web/services/terms-of-use";
import Creator from "./creator";
import type TherapeuticalArea from "./therapeutical-area";

export default class MedicModel extends Creator {
  @service declare termsOfUse: TermsOfUseService;

  // Roles that should not be shown on front
  static nonDisplayableRoles = [
    "contract_research_organisation",
    "third_party_server",
    "test_bot",
    "internal_server",
    "waiting",
    "nothing"
  ];

  @attr("string") declare role: string;

  @attr("string") declare email: string;
  @attr("string") declare phone: string;
  @attr("string") declare fax: string;
  @attr("string") declare address: string;
  @attr("string") declare city: string;
  @attr("string") declare postcode: string;
  @attr("string") declare professionalId: string;
  // IDNPS is the Identifiant National de Professionnel de Santé
  @attr("string") declare idnps: string;
  @attr("string") declare secureEmail: string;

  @attr("string") declare practiceLocation?: string;
  @attr("string") declare practiceSetting: string;
  @attr("number") declare acceptedTermsOfUseVersion: number;
  @attr("string") declare forceFinessForCnamRetrieval: string;
  @attr("string") declare dmpAdapterName: string;

  @attr("date") declare lastDmpDocumentsSearchDate: Date;
  @attr("boolean") declare canUseDmpRetrievalUpperLimit: boolean;

  @attr("string") declare forceIdentifierForInsRetrieval: string;
  @attr("boolean") declare insAccessDisabled: boolean;

  @attr("boolean") declare twoFactorAuthenticationEnabled: boolean;
  @attr("date") declare passwordChangedAt: Date;
  @attr("boolean") declare isSuperUser: boolean;
  @attr("date") declare createdAt: Date;

  @attr() declare days: string[];
  @attr("number") declare preferredTime: number;
  @attr() declare disabledNotificationTypes: string[];
  @attr() declare disabledAlertTypes: string[];

  @attr("string") declare professionalCategory: string;
  @attr() declare specialities: string[];
  @attr("boolean") declare refreshTokensEnabled: boolean;

  @attr("boolean") declare isInDiabetowiseTreatmentGroup: boolean;
  @attr("string") declare aphCode: string;

  /** A flag to activate matchlab bio matching page */
  @attr("boolean") declare useNewBioMatchingPage: boolean;

  /** A flag to activate medics fetch on /api/v2 */
  @attr("boolean") declare useMedicsV2: boolean;

  @attr({
    defaultValue() {
      return {};
    }
  })
  declare associatedData: Record<string, unknown>;

  @hasMany("health-centre", { async: false, inverse: null })
  declare healthCentres: HealthCentre[];

  @hasMany("medics-patient", { async: false, inverse: "medic" })
  declare medicsPatients: MedicsPatient[];

  isMedic = true;

  get lastPasswordEditDate(): Date {
    return this.passwordChangedAt || this.createdAt;
  }

  get shouldChangePassword(): boolean {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    return this.lastPasswordEditDate < oneYearAgo;
  }

  /**
   * Check if the current user is one of the given roles.
   * @return true if the current user is one of the roles passed as parameter
   * @example
   * medic.hasRole("doctor", "contract_research_organisation", "nurse_lead")
   */
  hasRole(...args: Array<string>): boolean {
    return args.includes(this.role);
  }

  get isAcceptedTermsOfUse(): boolean {
    return this.acceptedTermsOfUseVersion == this.termsOfUse.currentVersion;
  }

  get primaryHealthCenter(): HealthCentre | undefined {
    return this.healthCentres[0];
  }

  get therapeuticalArea(): TherapeuticalArea | null {
    return this.primaryHealthCenter?.therapeuticalArea ?? null;
  }

  get therapeuticalAreas() {
    return this.healthCentres.map(hc => hc.therapeuticalArea);
  }

  get therapeuticalAreaIds() {
    return this.therapeuticalAreas.map(area => area?.id);
  }

  get followUpReasonIdsFromHealthCentres(): string[] {
    return [
      ...new Set(
        this.therapeuticalAreas
          .map(ta => ta?.followUpReasons.map(fur => fur.id))
          .flat()
      )
    ] as string[];
  }

  get initials(): string {
    return [this.firstName?.charAt(0), this.lastName?.charAt(0)]
      .join("")
      .trim();
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    medic: MedicModel;
  }
}
